/**
 * Configuration for Local Listing VAT Number Import feature.
 * Countries listed here will show the VAT Number Import section
 * in the Local Listing Open Returns Details page.
 */

import { FeatureFlagEnum } from './enums';

/**
 * Countries that support Local Listing VAT Number import functionality.
 * Add country codes to this array to enable the feature for those countries.
 */
export const LOCAL_LISTING_IMPORT_SUPPORTED_COUNTRIES = ['BE'];

/**
 * Maximum file size for upload in MB
 */
export const MAX_UPLOAD_FILE_SIZE_MB = 2;

/**
 * Allowed file extensions for upload
 * Note: Both .xlsx and .xls are supported for compatibility
 */
export const ALLOWED_FILE_EXTENSIONS = ['.xlsx', '.xls'];

/**
 * Template filename for download
 */
export const LOCAL_LISTING_IMPORT_TEMPLATE_FILENAME = 'local-listing-import-template.xls';

/**
 * Listing type mapping
 * 0 = Out (Sales), 1 = In (Purchases)
 * Note: Numeric keys (0, 1) are automatically converted to strings in JS objects,
 * so '0' covers both string '0' and number 0 lookups.
 */
export const LISTING_TYPE_MAPPING = {
  'Out': 0,
  'out': 0,
  'OUT': 0,
  '0': 0,
  'In': 1,
  'in': 1,
  'IN': 1,
  '1': 1
};

/**
 * Maps In/Out string to numeric listing type
 * 
 * @param {string|number} inOutValue - The In/Out value from template
 * @returns {number} - The listing type (0 for Out/Sales, 1 for In/Purchases)
 */
export const mapListingTypeFromTemplate = (inOutValue) => {
  return LISTING_TYPE_MAPPING[inOutValue] ?? 0;
};

/**
 * Checks if Local Listing VAT Number Import is enabled for a specific country.
 * 
 * @param {string} countryCode - The country code (e.g., 'BE')
 * @param {object} featureFlags - The feature flags object from Redux state
 * @returns {boolean} True if feature flag is enabled AND country is supported
 */
export const isLocalListingImportEnabled = (countryCode, featureFlags) => {
  return featureFlags?.[FeatureFlagEnum.ENABLE_LOCAL_LISTING_IMPORT] === true &&
         LOCAL_LISTING_IMPORT_SUPPORTED_COUNTRIES.includes(countryCode?.toUpperCase());
};

export default {
  LOCAL_LISTING_IMPORT_SUPPORTED_COUNTRIES,
  LISTING_TYPE_MAPPING,
  MAX_UPLOAD_FILE_SIZE_MB,
  ALLOWED_FILE_EXTENSIONS,
  LOCAL_LISTING_IMPORT_TEMPLATE_FILENAME,
  mapListingTypeFromTemplate,
  isLocalListingImportEnabled
};
